import { useMemo } from 'react';
import { Search } from 'lucide-react';

import type { RetryAttempt, RetryStatus } from '@/api/retry-processor/mock';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { StatusChip } from './status-chip';

// Client-side searchable table — filters the already-loaded `attempts` in memory
// (no network call), mirroring the delivery-log screen's requirement. Local
// search state; no shared table component assumed (parallel build).
const dateFmt = new Intl.DateTimeFormat('en-US', {
	month: 'short',
	day: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
});

function formatTs(iso: string | null): string {
	if (!iso) return '—';
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? '—' : dateFmt.format(d);
}

export function AttemptsTable({
	attempts,
	onRowClick,
	query,
	onQueryChange,
	statusFilter,
}: {
	attempts: RetryAttempt[];
	onRowClick?: (attempt: RetryAttempt) => void;
	query: string;
	onQueryChange: (q: string) => void;
	statusFilter: RetryStatus[] | null;
}) {
	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		let result = attempts;
		if (q) {
			result = result.filter((a) =>
				[a.recipient, a.provider, a.status, a.errorReason].some((field) =>
					field.toLowerCase().includes(q),
				),
			);
		}
		if (statusFilter) {
			result = result.filter((a) => statusFilter.includes(a.status));
		}
		return result;
	}, [attempts, query, statusFilter]);

	return (
		<section className="rounded-lg border border-border bg-card">
			<header className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="font-serif text-lg font-bold text-foreground">
						Retry Attempts
					</h2>
					<p className="text-sm text-muted-foreground">
						{filtered.length} of {attempts.length} attempts
					</p>
				</div>
				<div className="relative w-full sm:w-72">
					<Search
						className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
						aria-hidden="true"
					/>
					<input
						type="search"
						value={query}
						onChange={(e) => onQueryChange(e.target.value)}
						placeholder="Search recipient, provider, reason…"
						aria-label="Search retry attempts"
						className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none"
					/>
				</div>
			</header>

			{filtered.length === 0 ? (
				<div className="flex flex-col items-center gap-2 py-16 text-center">
					<Search className="size-8 text-muted-foreground" aria-hidden="true" />
					<p className="text-sm text-muted-foreground">
						No attempts match “{query}”.
					</p>
				</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow className="font-mono text-xs uppercase tracking-wide text-muted-foreground hover:bg-transparent">
							<TableHead className="px-4 py-3">Recipient</TableHead>
							<TableHead className="px-4 py-3">Provider</TableHead>
							<TableHead className="px-4 py-3">Status</TableHead>
							<TableHead className="px-4 py-3">Attempt</TableHead>
							<TableHead className="px-4 py-3">Error Reason</TableHead>
							<TableHead className="px-4 py-3">Last Attempt</TableHead>
							<TableHead className="px-4 py-3">Next Retry</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{filtered.map((a) => (
							<TableRow
									key={a.id}
									onClick={onRowClick ? () => onRowClick(a) : undefined}
									className={
										onRowClick
											? 'cursor-pointer hover:bg-accent'
											: 'hover:bg-accent'
									}
								>
								<TableCell className="px-4 py-3 font-medium text-foreground">
									{a.recipient}
								</TableCell>
								<TableCell className="px-4 py-3 text-muted-foreground">
									{a.provider}
								</TableCell>
								<TableCell className="px-4 py-3">
									<StatusChip status={a.status} />
								</TableCell>
								<TableCell className="px-4 py-3 font-mono tabular-nums text-muted-foreground">
									{a.attempt}/{a.maxAttempts}
								</TableCell>
								<TableCell className="px-4 py-3 text-muted-foreground">
									{a.errorReason}
								</TableCell>
								<TableCell className="px-4 py-3 font-mono tabular-nums text-muted-foreground">
									{formatTs(a.lastAttempt)}
								</TableCell>
								<TableCell className="px-4 py-3 font-mono tabular-nums text-muted-foreground">
									{formatTs(a.nextRetry)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</section>
	);
}
